import React from "react";
import { getAvatarColor, getInitials } from "../Meetings/Meetings.utils";
import {
  formatTime,
  resolveAuthor,
  resolveMentions,
} from "./Conversation.utils";
import ThreadReplyItem from "./ThreadReplyItem";
import LoadingComponent from "../../components/common/LoadingComponent";
import { useConversationContext } from "./ConversationContext";
import { Icons } from "../../constants/icon-library";
import { INTEGRATION_ENUM } from "../../constants/constants";

function MessageItem({
  messageData,
  replyProps,
  threadProps,
  showActions = true,
  flatConversation = false,
  showThreadActions,
}) {
  const { authorMap, meta } = useConversationContext();
  const { isCommentIntegration } = meta;
  const { msg, authorName, showGroupLabel } = messageData;

  const {
    replyOpen,
    replyDraft,
    onToggleReply,
    onReplyChange,
    onSendReply,
    isSendingReply,
  } = replyProps;

  const {
    threadReplyState,
    onToggleThreadReply,
    onThreadReplyChange,
    onSendThreadReply,
  } = threadProps;

  const msgId = String(msg.externalMessageId || msg.uuid || msg._id);

  const isTwilio = msg.platform?.toLowerCase() === INTEGRATION_ENUM.TWILIO;
  const isIntercom = msg.platform?.toLowerCase() === INTEGRATION_ENUM.INTERCOM;
  const isReadOnlyPlatform =
    msg.platform?.toLowerCase() ===
      INTEGRATION_ENUM.FACEBOOK_READONLY_MESSAGES ||
    msg.platform?.toLowerCase() ===
      INTEGRATION_ENUM.INSTAGRAM_READONLY_MESSAGES;

  const threads = isTwilio
    ? []
    : (msg.threads || []).filter((t) => t.text?.trim());

  const hasThreads = !isTwilio && threads.length > 0;

  if (flatConversation) {
    const allMessages = [
      {
        id: msgId,
        text: msg.text,
        author: msg.author,
        sentAt: msg.sentAt,
        link: msg.link,
        raw: msg,
        isMain: true,
        isSender: msg.isSender,
      },

      ...threads.map((thread, ti) => ({
        id: thread.externalMessageId || `${msgId}-thread-${ti}`,
        text: thread.text,
        author: thread.author,
        sentAt: thread.sentAt,
        link: thread.link,
        raw: thread,
        isMain: false,
        isSender: thread.isSender,
      })),
    ].sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt));

    return (
      <div className="d-flex flex-column gap-3">
        {allMessages.map((message) => {
          const resolvedSender = resolveAuthor(message.author, authorMap);

          const replyState = message.isMain
            ? {
                open: replyOpen,
                draft: replyDraft,
                isSending: isSendingReply,
              }
            : threadReplyState?.[message.id] || {};

          const toggleReply = message.isMain
            ? onToggleReply
            : onToggleThreadReply;

          const changeReply = message.isMain
            ? onReplyChange
            : onThreadReplyChange;

          const sendReply = message.isMain ? onSendReply : onSendThreadReply;

          return (
            <div
              key={message.id}
              className={`d-flex align-items-start gap-2 ${
                isTwilio
                  ? message.isSender
                    ? "justify-content-start"
                    : "justify-content-end"
                  : ""
              }`}
            >
              <div
                className="d-flex align-items-start gap-2"
                style={
                  isTwilio
                    ? {
                        maxWidth: "80%",
                        flexDirection: message.isSender ? "row" : "row-reverse",
                      }
                    : undefined
                }
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0 text-white conv-avatar-md"
                  style={{
                    backgroundColor: getAvatarColor(resolvedSender),
                  }}
                >
                  {getInitials(resolvedSender)}
                </div>

                <div className="position-relative flex-grow-1 min-w-0 conv-message-bubble-wrap">
                  <div
                    className="min-w-0 d-inline-flex flex-column"
                    style={{ maxWidth: "100%" }}
                  >
                    <div className="d-flex align-items-baseline gap-2 mb-1 flex-wrap">
                      <span
                        className="fw-semibold conv-author-name-lg"
                        style={{
                          color: getAvatarColor(resolvedSender),
                        }}
                      >
                        {resolvedSender}
                      </span>

                      {message.sentAt && (
                        <small className="text-body-secondary conv-time-sm">
                          {formatTime(message.sentAt)}
                        </small>
                      )}
                    </div>

                    <div
                      className="position-relative d-inline-block"
                      style={{
                        width: "fit-content",
                        maxWidth: "100%",
                      }}
                    >
                      <div className="shadow-sm bg-theme text-body conv-bubble">
                        <div className="conv-bubble-text">
                          {resolveMentions(message.text, authorMap)}
                        </div>
                      </div>

                      <div
                        className="d-flex gap-1"
                        style={{
                          position: "absolute",
                          right: "-10px",
                          bottom: "-10px",
                        }}
                      >
                        {isCommentIntegration && message.link && (
                          <a
                            href={message.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn p-0 bg-body shadow-sm border-0 conv-action-btn-sm"
                            title="View on platform"
                          >
                            <i className={`wb ${Icons("view")} me-1`} />
                          </a>
                        )}

                        {!isTwilio && !isIntercom && !isReadOnlyPlatform && (
                          <button
                            type="button"
                            className="btn p-0 bg-body shadow-sm border-0 conv-action-btn-sm conv-reply-corner-btn"
                            title="Reply"
                            onClick={() => toggleReply(message.id)}
                          >
                            <i className="bi bi-reply text-body-secondary" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {!isTwilio && replyState.open && (
                    <div className="mt-2 d-flex gap-2 conv-reply-row">
                      <input
                        type="text"
                        className="form-control form-control-sm bg-theme border-0 rounded-pill px-3 conv-reply-input"
                        placeholder={`Reply to ${resolvedSender}`}
                        value={replyState.draft || ""}
                        onChange={(e) =>
                          changeReply(message.id, e.target.value)
                        }
                        disabled={replyState.isSending}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();

                            sendReply(message.raw, message.id);
                          }
                        }}
                        autoFocus
                      />

                      <button
                        type="button"
                        className="btn btn-sm btn-primary rounded-pill px-3 flex-shrink-0 d-flex align-items-center justify-content-center"
                        onClick={() => sendReply(message.raw, message.id)}
                        disabled={
                          !replyState.draft?.trim() || replyState.isSending
                        }
                      >
                        {replyState.isSending ? (
                          <span className="conv-spinner-wrap">
                            <LoadingComponent
                              loading={true}
                              size={6}
                              wrapperStyle={{
                                padding: 0,
                                lineHeight: 0,
                                display: "flex",
                                flexDirection: "row",
                                whiteSpace: "nowrap",
                              }}
                            />
                          </span>
                        ) : (
                          <i className="bi bi-send-fill" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const canShowThreadActions =
    showThreadActions !== undefined ? showThreadActions : showActions;

  return (
    <div>
      <div
        className={`d-flex align-items-start gap-2 mt-3 ${
          isTwilio
            ? msg.isSender
              ? "justify-content-start"
              : "justify-content-end"
            : ""
        }`}
      >
        <div
          className="d-flex align-items-start gap-2"
          style={
            isTwilio
              ? {
                  maxWidth: "80%",
                  flexDirection: msg.isSender ? "row" : "row-reverse",
                }
              : undefined
          }
        >
          <div
            className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0 text-white conv-avatar-md"
            style={{
              backgroundColor: getAvatarColor(authorName),
            }}
          >
            {getInitials(authorName)}
          </div>

          <div className="position-relative flex-grow-1 min-w-0 conv-message-bubble-wrap">
            <div className="min-w-0">
              <div className="d-flex align-items-baseline gap-2 mb-1 flex-wrap">
                <span
                  className="fw-semibold conv-author-name-lg"
                  style={{
                    color: getAvatarColor(authorName),
                  }}
                >
                  {authorName}
                </span>

                {showGroupLabel && msg._groupLabel && (
                  <span className="badge bg-primary-subtle text-primary-emphasis rounded-pill fw-normal conv-group-badge">
                    #{msg._groupLabel}
                  </span>
                )}

                {msg.sentAt && (
                  <small className="text-body-secondary conv-time-sm">
                    {formatTime(msg.sentAt)}
                  </small>
                )}
              </div>

              <div className="shadow-sm bg-theme text-body conv-bubble">
                <div className="conv-bubble-text">
                  {resolveMentions(msg.text, authorMap)}
                </div>
              </div>
            </div>

            <div className="conv-msg-actions d-flex gap-1">
              {isCommentIntegration && msg.link && (
                <a
                  href={msg.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn p-0 bg-body shadow-sm border-0 conv-action-btn-sm d-flex align-items-center justify-content-center"
                  title="View on platform"
                >
                  <i
                    className={`wb ${Icons("view")}`}
                    style={{
                      transform: "translateY(2px)",
                    }}
                  />
                </a>
              )}

              {!isTwilio && !isIntercom && !isReadOnlyPlatform &&(
                <button
                  type="button"
                  className="btn p-0 bg-body shadow-sm border-0 conv-action-btn-sm"
                  title="Reply"
                  onClick={() => onToggleReply(msgId)}
                >
                  <i className="bi bi-reply text-body-secondary" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isTwilio && replyOpen && (
        <div className="mt-3 d-flex gap-2 ms-5 conv-reply-row">
          <input
            type="text"
            className="form-control form-control-sm bg-theme border-0 rounded-pill px-3 conv-reply-input"
            placeholder={`Reply to ${authorName}`}
            value={replyDraft || ""}
            onChange={(e) => onReplyChange(msgId, e.target.value)}
            disabled={isSendingReply}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();

                onSendReply(msg, msgId);
              }
            }}
          />

          <button
            type="button"
            className="btn btn-sm btn-primary rounded-pill px-3 flex-shrink-0 d-flex align-items-center justify-content-center conv-reply-send-btn"
            onClick={() => onSendReply(msg, msgId)}
            disabled={!replyDraft?.trim() || isSendingReply}
          >
            {isSendingReply ? (
              <span className="conv-spinner-wrap">
                <LoadingComponent
                  loading={true}
                  size={6}
                  wrapperStyle={{
                    padding: 0,
                    lineHeight: 0,
                    display: "flex",
                    flexDirection: "row",
                    whiteSpace: "nowrap",
                  }}
                />
              </span>
            ) : (
              <i className="bi bi-send-fill" />
            )}
          </button>
        </div>
      )}

      {hasThreads && (
        <div className="mt-3 d-flex flex-column gap-2 ms-5 conv-threads-wrap">
          <small className="text-primary fw-semibold d-flex align-items-center conv-threads-label">
            <i className="bi bi-arrow-return-right me-1" />
            {threads.length} {threads.length === 1 ? "reply" : "replies"}
          </small>

          {threads.map((thread, ti) => {
            const tId = thread.externalMessageId || `${msgId}-thread-${ti}`;

            const replyState = threadReplyState?.[tId] || {};

            const threadReplyProps = {
              replyOpen: replyState.open,
              replyDraft: replyState.draft,
              isSendingReply: replyState.isSending,
              onToggleReply: onToggleThreadReply,
              onReplyChange: onThreadReplyChange,
              onSendReply: onSendThreadReply,
            };

            return (
              <ThreadReplyItem
                key={tId}
                thread={thread}
                threadId={tId}
                replyProps={threadReplyProps}
                showActions={canShowThreadActions}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MessageItem;
